import { create } from 'zustand';
import type { Room } from 'colyseus.js';
import { colyseusClient } from '../api/colyseus.js';
import type { CreateRoomConfig } from '../components/room-hub/CreateRoomPanel.js';

interface PlayerDetails {
  id: string;
  displayName: string;
  avatarUrl: string;
  isReady: boolean;
  isHost: boolean;
}

interface RoomDetails {
  name: string;
  code: string;
  maxPlayers: number;
  gameSpeed: string;
  callerMode: string;
  prizePreset: string;
  allowLateMarking: boolean;
  callInterval: number;
  disqualifyOnWrongClick: boolean;
}

interface RoomState {
  activeRoom: Room | null;
  roomDetails: RoomDetails | null;
  players: PlayerDetails[];
  isLoading: boolean;
  error: string | null;
  gameStarted: boolean;

  createRoom: (config: CreateRoomConfig, displayName: string) => Promise<void>;
  joinRoom: (code: string, displayName: string) => Promise<void>;
  leaveRoom: () => void;
  startGame: () => void;
  setupRoomListeners: (room: Room) => void;
}

export const useRoomStore = create<RoomState>((set, get) => ({
  activeRoom: null,
  roomDetails: null,
  players: [],
  isLoading: false,
  error: null,
  gameStarted: false,

  createRoom: async (config, displayName) => {
    set({ isLoading: true, error: null });
    try {
      const room = await colyseusClient.create('game', {
        ...config,
        displayName,
      });
      localStorage.setItem('active_room_code', room.id);
      set({ activeRoom: room });
      get().setupRoomListeners(room);
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : 'Failed to create room';
      set({ error: errMsg, isLoading: false });
    }
  },

  joinRoom: async (code, displayName) => {
    set({ isLoading: true, error: null });
    try {
      const room = await colyseusClient.joinById(code, {
        displayName,
      });
      localStorage.setItem('active_room_code', room.id);
      set({ activeRoom: room });
      get().setupRoomListeners(room);
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : 'Failed to join room';
      set({ error: errMsg, isLoading: false });
    }
  },

  leaveRoom: () => {
    const { activeRoom } = get();
    if (activeRoom) {
      activeRoom.leave();
    }
    localStorage.removeItem('active_room_code');
    set({ activeRoom: null, roomDetails: null, players: [], gameStarted: false });
  },

  startGame: () => {
    const { activeRoom } = get();
    if (activeRoom) {
      activeRoom.send('start-game', {});
    }
  },

  setupRoomListeners: (room: Room) => {
    // Listen for server-broadcast 'room-sync' messages (plain JSON, bypasses schema)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    room.onMessage('room-sync', (payload: any) => {
      const isOngoing = payload.roomDetails.gameStatus === 'active' || payload.roomDetails.gameStatus === 'paused' || payload.roomDetails.gameStatus === 'countdown';
      set({
        roomDetails: payload.roomDetails,
        players: payload.players,
        isLoading: false,
        gameStarted: isOngoing ? true : get().gameStarted,
      });
    });

    // Listen for game-started broadcast from server
    room.onMessage('game-started', () => {
      localStorage.setItem('active_room_code', room.id);
      set({ gameStarted: true });
    });

    // Clean up storage if game is completed
    room.onMessage('game-completed', () => {
      localStorage.removeItem('active_room_code');
    });

    room.onError((code, message) => {
      console.error('[RoomStore] Room error:', code, message);
      set({ error: message || `Room error code: ${code}`, isLoading: false });
    });
  },
}));
